export const classicTags = [
    '2.2',
    '2.1',
    '2.0',
    '1.9PS',
    '1.9',
    '1.8',
    '1.7',
    '1.6PS',
    '1.6',
    '1.5',
    'Medium',
    'Long',
    'XL',
    'XXL',
    'XXL+',
    'NONG',
    '2P',
    'Circles',
    'Clicksync',
    'Fast-Paced',
    'Timings',
    'Chokepoints',
    'Unbalanced',
    'Learny',
    'Memory',
    'High CPS',
    'Gimmicky',
    'Flow',
    'Slow-Paced',
    'Bossfight',
    'Mirror',
    'Nerve Control',
    'Cube',
    'Ship',
    'Ball',
    'UFO',
    'Wave',
    'Robot',
    'Spider',
    'Old Swing',
    'New Swing',
    'Duals',
    'Overall',
] as const;

export type ClassicTag = (typeof classicTags)[number];

export const ClassicTagDescriptions: Record<ClassicTag, string> = {
    '2.2': 'Extreme demons released in update 2.2.',
    '2.1': 'Extreme demons released in update 2.1.',
    '2.0': 'Extreme demons released in update 2.0.',
    '1.9PS': 'Extreme demons released on the 1.9 private server.',
    '1.9': 'Extreme demons released in update 1.9.',
    '1.8': 'Extreme demons released in update 1.8.',
    '1.7': 'Extreme demons released in update 1.7.',
    '1.6PS': 'Extreme demons released on the 1.6 private servers.',
    '1.6': 'Extreme demons released in update 1.6.',
    '1.5': 'Extreme demons released in update 1.5.',
    Medium: 'Extreme demons that are medium in length (30–59 seconds).',
    Long: 'Extreme demons that are long in length (1–1:59 minutes).',
    XL: 'Extreme demons that are extra long in length (2–2:59 minutes).',
    XXL: 'Extreme demons that are between 3–5 minutes in length.',
    'XXL+': 'Extreme demons that are over 5 minutes long.',
    NONG: 'Extreme demons that use songs not found on Newgrounds.',
    '2P': 'Extreme demons designed for two players.',
    Circles: 'Extreme demons using the Nine Circles theme.',
    Clicksync: 'Extreme demons with gameplay synced to the music.',
    'Fast-Paced': 'Extreme demons with fast-paced gameplay.',
    Timings:
        'Extreme demons that focus heavily on precise timings (cube, ball, UFO, robot, spider).',
    Chokepoints:
        'Extreme demons with particularly difficult clicks that commonly cause deaths.',
    Unbalanced:
        'Extreme demons with particularly difficult sections that are hard to pass.',
    Learny: 'Extreme demons that take longer than usual to memorize.',
    Memory: 'Extreme demons with gameplay that can’t be easily read visually.',
    'High CPS': 'Extreme demons requiring a high number of clicks per second.',
    Gimmicky:
        'Extreme demons with unconventional or unique gameplay mechanics.',
    Flow: 'Extreme demons where the gameplay flows well with the music.',
    'Slow-Paced': 'Extreme demons with slower-paced gameplay.',
    Bossfight: 'Extreme demons that include boss fights.',
    Mirror: 'Extreme demons with mirrored gameplay for most of the level.',
    'Nerve Control':
        'Extreme demons that challenge a player’s ability to stay calm near the end.',
    Cube: 'Extreme demons that feature mostly cube gameplay.',
    Ship: 'Extreme demons that feature mostly ship gameplay.',
    Ball: 'Extreme demons that feature mostly ball gameplay.',
    UFO: 'Extreme demons that feature mostly UFO gameplay.',
    Wave: 'Extreme demons that feature mostly wave gameplay.',
    Robot: 'Extreme demons that feature mostly robot gameplay.',
    Spider: 'Extreme demons that feature mostly spider gameplay.',
    'Old Swing':
        'Extreme demons with old swing gameplay, usually involving a ball dual where one ball controls the other.',
    'New Swing': 'Extreme demons that feature modern swing gameplay.',
    Duals: 'Extreme demons that feature mostly dual gameplay.',
    Overall: 'Extreme demons with a balanced mix of all gameplay types.',
};

export const platTags = [
    '2.2',
    'XXL',
    'NONG',
    '2P',
    'Needle',
    'Autoscroll',
    'Minigame',
    'Gimmicky',
    'Deathless',
    'Memory',
    'Slopetech',
    'Dashtech',
    'Orbs',
    'Speedrun',
    'Fixed Hitboxes',
    'Low-Gravity',
    'Cycle-Based',
    'Fast-Paced',
    'Slow-Paced',
    'Cube',
    'Robot',
    'UFO',
    'Ball',
    'Jetpack',
    'Overall',
] as const;

export type PlatTag = (typeof platTags)[number];

export const PlatTagDescriptions: Record<PlatTag, string> = {
    '2.2': 'Extreme demons released in update 2.2.',
    NONG: 'Extreme demons that use songs not found on Newgrounds.',
    Overall: 'Extreme demons with a balanced mix of all gameplay types.',
    Cube: 'Extreme demons that feature mostly cube gameplay.',
    Ball: 'Extreme demons that feature mostly ball gameplay.',
    UFO: 'Extreme demons that feature mostly UFO gameplay.',
    Robot: 'Extreme demons that feature mostly robot gameplay.',
    Jetpack: 'Extreme demons that feature mostly jetpack gameplay.',
    Needle: 'Extreme demons that require high precision.',
    Autoscroll:
        'Extreme demons where the player must keep up with camera movement.',
    Minigame: "Extreme demons that don't use GD's normal gameplay mechanics",
    Gimmicky:
        'Extreme demons with unconventional or unique gameplay mechanics.',
    Deathless: 'Extreme demons with no checkpoints between areas.',
    Memory: 'Extreme demons with gameplay that can’t be easily read visually.',
    '2P': 'Extreme demons that require two sets of inputs.',
    Slopetech: 'Extreme demons that require slopeboosting.',
    Dashtech: 'Extreme demons that have dash orbs in rapid succession.',
    Orbs: 'Extreme demons that have orb clicks as its main difficulty.',
    'Fast-Paced': 'Extreme demons with fast-paced gameplay.',
    XXL: 'Extreme demons that require a high amount of playtime to complete.',
    Speedrun:
        'Extreme demons that limit the amount of time to beat the level, usually by a timer or kill-wall.',
    'Cycle-Based':
        'Extreme demons where some gameplay follows a looping rhythm.',
    'Fixed Hitboxes':
        'Extreme demons where all the spikes hitboxes are more accurate to the visuals than normal.',
    'Slow-Paced': 'Extreme demons with usually very tight and slow gameplay.',
    'Low-Gravity':
        'Extreme demons that feature low gravity sections of gameplay.',
};

export const allTags = [...classicTags, ...platTags] as const;
export const allTagDescriptions: Record<(typeof allTags)[number], string> = {
    ...ClassicTagDescriptions,
    ...PlatTagDescriptions,
};

export type Tag = ClassicTag | PlatTag;
export const sortTags = (a: Tag, b: Tag): number => {
    return allTags.indexOf(a) - allTags.indexOf(b);
};
